/**
 * @file Onboarding access request form.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pick from 'lodash.pick';
import get from 'lodash.get';
import { Redirect } from 'react-router-dom';

import Button from '../../global/Button';
import Alert from '../../global/Alert';
import FormGroup from '../../global/FormGroup';
import PhoneInput from '../../global/PhoneInput';
import InputGroup from '../../global/InputGroup';
import { getErrorString } from '../../../util/normalize-errors';
import { getAsset } from '../../../util/assets';
import { isValidPhone } from '../../../util/phone';
import ContextualString from '../../contextual/ContextualString';
import { getContextualString } from '../../../util/context-strings';

class OnboardingAccessRequestForm extends Component {
  static propTypes = {
    albumFolderAccessRequest: PropTypes.func.isRequired,
    user: PropTypes.shape({
      id: PropTypes.string.isRequired,
      phone_number: PropTypes.string,
      email_address: PropTypes.string,
    }),
    albumFolder: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    redirectUrl: PropTypes.string,
  };

  state = {
    // Store userId in state so that we can compare it in getDerivedStateFromProps
    userId: get(this.props.user, 'id') || '',
    name: '',
    phoneNumber: get(this.props.user, 'phone_number') || '',
    emailAddress: get(this.props.user, 'email_address') || '',
    identityName: '',
    identityRelation: '',
    comments: '',
    submitting: false,
    success: false,
    error: '',
    useRedirectUrl: false,
  };

  static getDerivedStateFromProps(props, state) {
    const { user } = props;
    const { userId } = state;

    if (!userId && user) {
      return {
        userId: user.id,
        phoneNumber: user.phone_number || '',
        emailAddress: user.email_address || '',
      };
    }

    return null;
  }

  onInputChange = event => {
    const { name, value } = event.target;

    this.setState(() => ({
      [name]: value,
    }));
  };

  onFormSubmit = async event => {
    if (event) {
      event.preventDefault();
    }

    const { name } = this.state;
    const { albumFolderAccessRequest, albumFolder } = this.props;

    this.setState(() => ({ error: '', success: false, submitting: true }));

    try {
      const [firstName, ...lastName] = name.split(' ').map(s => s.trim());

      const {
        data: {
          albumFolderAccessRequest: { auto_added_members: autoAddedMembers },
        },
      } = await albumFolderAccessRequest({
        albumFolderId: albumFolder.id,
        ...pick(this.state, [
          'name',
          'emailAddress',
          'identityName',
          'identityRelation',
          'phoneNumber',
          'comments',
        ]),
        firstName,
        lastName: lastName.join(' '),
      });

      this.setState(() => ({
        success: true,
        useRedirectUrl: autoAddedMembers,
        submitting: false,
      }));
    } catch (error) {
      this.setState(() => ({
        error: getErrorString(error),
        success: false,
        submitting: false,
      }));
    }
  };

  render() {
    const { albumFolder, user, redirectUrl } = this.props;
    const {
      phoneNumber,
      emailAddress,
      name,
      identityName,
      submitting,
      error,
      success,
      useRedirectUrl,
    } = this.state;

    if (!albumFolder) {
      return null;
    }

    if (success && redirectUrl && useRedirectUrl) {
      return <Redirect to={redirectUrl} />;
    }

    if (success) {
      return (
        <div className="text-center">
          <img
            src={getAsset('/waldo-logo.svg')}
            alt=""
            width="110"
            className="mb-3"
          />

          <h3 className="mb-1">Thank you!</h3>
          <p className="m-0">
            We&rsquo;ve got your request and we&rsquo;ll be in touch soon!
          </p>
        </div>
      );
    }

    const accountName =
      get(albumFolder, 'account.organization_name') ||
      get(albumFolder, 'account.name');

    const disablePhoneNumber = !!user?.phone_number;
    const disableEmail = !!user?.email_address;

    return (
      <form method="post" onSubmit={this.onFormSubmit}>
        <div className="mb-3">
          <img
            src={getAsset('/waldo-icon.svg')}
            alt=""
            width="50"
            className="mb-2"
          />
          <p>
            <ContextualString
              id="accessRequestFormParentMessage"
              defaultMessage="Hi, Waldo here! The {eventName} gallery is private and only accessible by the users of {accountName}. The mobile number you provided is either not in the {eventName}’s records or you have withheld consent for 3rd parties to access your information. Please complete the form below to give consent."
              type={albumFolder.album_type}
              subtype={albumFolder.album_subtype}
              values={{
                eventName: albumFolder.name,
                accountName,
              }}
            />
          </p>
        </div>

        <hr />

        <div className="row">
          <div className="col-12">
            <FormGroup
              id="name"
              label={getContextualString({
                id: 'accessRequestFormNameLabel',
                type: albumFolder.album_type,
                subtype: albumFolder.album_subtype,
                defaultMessage: 'Your signature',
              })}
              name="name"
              value={name}
              onChange={this.onInputChange}
              required
              placeholder="Type your full name here..."
              labelNote="required"
              className="mb-2"
            />
          </div>

          <div className="col-lg-6 col-12">
            <InputGroup
              id="email"
              type="email"
              label={getContextualString({
                id: 'accessRequestFormEmailLabel',
                type: albumFolder.album_type,
                subtype: albumFolder.album_subtype,
                defaultMessage: 'Your email address',
              })}
              addon="@"
              name="emailAddress"
              value={emailAddress}
              onChange={this.onInputChange}
              required={!phoneNumber}
              labelNote={
                !phoneNumber || !isValidPhone(phoneNumber) ? 'required' : ''
              }
              wrapperClassName="mb-2"
              disabled={disableEmail}
            />
          </div>

          <div className="col-lg-6 col-12">
            <PhoneInput
              id="phone-number"
              label={getContextualString({
                id: 'accessRequestFormPhoneNumberLabel',
                type: albumFolder.album_type,
                subtype: albumFolder.album_subtype,
                defaultMessage: 'Your phone number',
              })}
              value={phoneNumber || '+1'}
              onChange={value => this.setState(() => ({ phoneNumber: value }))}
              required
              labelNote="required"
              className="mb-2"
              disabled={disablePhoneNumber}
            />
          </div>

          <div className="col-12">
            {albumFolder.subtype !== null ? (
              <FormGroup
                id="identity-name-input"
                className="mb-2"
                label={getContextualString({
                  id: 'accessRequestFormIdentityNameLabel',
                  type: albumFolder.album_type,
                  subtype: albumFolder.album_subtype,
                  defaultMessage: 'Enter name',
                })}
                value={identityName}
                name="identityName"
                labelNote="required"
                onChange={this.onInputChange}
                required
                note={
                  <span className="text-sm">
                    Separate multiple names with a comma.
                  </span>
                }
              />
            ) : (
              <p>
                By submitting the following form I confirm I consent to Waldo’s
                accessing my photos for the purposes of delivering them to me.
              </p>
            )}
          </div>

          {error && (
            <div className="col-12">
              <Alert type="danger">{error}</Alert>
            </div>
          )}

          <div className="col-12">
            <Button
              type="submit"
              className="text-white-space-normal"
              btnBlock
              disabled={submitting}
            >
              {submitting ? (
                'Loading…'
              ) : (
                <ContextualString
                  id="accessRequestFormSubmitMessage"
                  defaultMessage="Yes, I would like Waldo to send me my identity’s {eventName} photos!"
                  type={albumFolder.album_type}
                  subtype={albumFolder.album_subtype}
                  values={{
                    eventName: albumFolder.name,
                  }}
                />
              )}
            </Button>
          </div>

          {albumFolder.subtype !== null && (
            <div className="col-12">
              <p className="mt-1">
                <ContextualString
                  id="accessRequestFormConsentText"
                  defaultMessage="By submitting the following form I confirm I’m the parent or legal guardian of the following persons and consent to Waldo accessing their photos for purposes of delivering them to me."
                  type={albumFolder.album_type}
                  subtype={albumFolder.album_subtype}
                />
              </p>
            </div>
          )}
        </div>
      </form>
    );
  }
}

export default OnboardingAccessRequestForm;
